<template>
  <v-form class="application-form" @submit.prevent="submitApplication">
    <v-card flat class="v-card--no-style">
      <v-card-title class="v-card__title">Twoje dane</v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12">
            <div class="v-card__description pl-0">
              <display-model-value :model="schema.application.firstName" />

              <display-model-value :model="schema.application.lastName" />

              <display-model-value :model="schema.application.birthDate" />

              <display-model-value :model="schema.application.phoneNumber" />

              <display-model-value :model="schema.application.email" :show-optional-info="false" />

              <div class="d-flex mt-2">
                <div>Adres:</div>

                <div class="font-bold mx-1">
                  {{ schema.application.$model | address }}
                  <span v-if="addressError" class="error--text"> Proszę uzupełnić adres </span>
                </div>
              </div>

              <div v-if="!newCardApplication && card && hasEndDate && !isInactive" class="d-flex mt-2">
                <div>Data ważności karty:</div>

                <div class="font-bold mx-1">{{ cardEndDate }}</div>
              </div>
            </div>
          </v-col>

          <v-col v-if="newCardApplication" cols="12">
            <p>
              <v-btn large min-width="250" outlined elevation="0" color="primary" :to="{ name: 'profileConfig' }">
                Zmień dane
              </v-btn>
            </p>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-col cols="12" md="8">
      <v-card flat class="v-card elevation-0 mb-3">
        <div class="v-card__title px-0">Formularz</div>

        <div class="v-card__content pt-4">
          <w-checkbox v-if="newCardApplication" disabled :model="schema.application.virtual" />

          <w-checkbox v-if="newCardApplication" :model="schema.application.physical" />

          <w-select
            v-if="!schema.application.collectionPointId.$schema.disabled && (!card || isInactive)"
            outlined
            item-text="name"
            item-value="id"
            type="number"
            class="mt-2"
            aria-required="true"
            :model="schema.application.collectionPointId"
            :items="collectionPoints"
          />

          <div class="media-information">
            <p class="media-information__title">
              Ważne
            </p>

            <template v-if="newCardApplication">
              <p class="media-information__content">
                Poniżej należy załączyć pliki z dokumentami poświadczającymi uprawnienie do uzyskania Usteckiej Karty
                Mieszkańca.
              </p>

              <p class="media-information__content">
                Brak załączników spowoduje odrzucenie wniosku.
              </p>
            </template>

            <p v-else class="media-information__content">
              Poniżej należy załączyć pliki z dokumentami poświadczającymi uprawnienie do przedłużenia ważności
              Usteckiej Karty Mieszkańca. Załączniki są wymagane.
            </p>

            <v-expansion-panels flat class="media-information__accordion-container">
              <v-expansion-panel>
                <v-expansion-panel-header expand-icon="none" class="media-information__accordion-activator">
                  <template v-slot:default="{ open }">
                    <span v-if="!open">Pokaż jakie to dokumenty </span>

                    <span v-else>Zwiń </span>
                  </template>
                </v-expansion-panel-header>

                <v-expansion-panel-content class="media-information__accordion-content">
                  <ul>
                    <li>
                      pierwszą stronę zeznania PIT za rok poprzedni, poświadczoną przez Urząd Skarbowy:
                      <ol type="a">
                        <li>
                          w przypadku składania zeznania PIT w Urzędzie Skarbowym - zawierającą pieczęć wpływu Urzędu;
                        </li>

                        <li>
                          w przypadku rozliczania PIT drogą elektroniczną – dodatkowo Urzędowe Poświadczenie Odbioru,
                          lub zaświadczenie z Urzędu Skarbowego potwierdzające fakt złożenia zeznania za rok poprzedni
                          ze wskazaniem Miasta Ustka jako miejsca zamieszkania podatnika;
                        </li>
                      </ol>
                    </li>

                    <li>
                      w przypadku osób nieosiągających dochodu – zaświadczenie z Urzędu Skarbowego potwierdzające
                      niezłożenie zeznania podatkowego ze względu na nieosiąganie dochodu za rok poprzedni, z
                      jednoczesną informacją o zarejestrowaniu w Urzędzie Skarbowym ze wskazaniem Miasta Ustka jako
                      miejsca zamieszkania podatnika;
                    </li>

                    <li>
                      w przypadku emerytów i rencistów nie składających deklaracji PIT w Urzędzie Skarbowym – zeznanie
                      PIT z Zakładu Ubezpieczeń Społecznych;
                    </li>

                    <li>
                      w przypadku dzieci do 18 roku życia – dokumenty jednego z rodziców/opiekunów prawnych wymienione w
                      pkt. 1-3 i 5-6;
                    </li>

                    <li>
                      w przypadku pełnoletnich osób uczących się nieosiągających dochodu – zaświadczenie podmiotu, w
                      którym pobierana jest nauka, potwierdzające kontynuację nauki, zawierające adres zamieszkania
                      ucznia/studenta;
                    </li>

                    <li>
                      w przypadku osób korzystających z pomocy społecznej, a nie składających deklaracji PIT -
                      zaświadczenie z Miejskiego Ośrodka Pomocy Społecznej w Ustce o korzystaniu z pomocy w roku
                      poprzednim i/lub bieżącym, w przypadku odbioru Karty w punkcie innym niż Miejski Ośrodek Pomocy
                      Społecznej w Ustce;
                    </li>

                    <li>
                      w przypadku opieki prawnej – orzeczenie właściwego sądu.
                    </li>
                  </ul>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>

          <template v-for="(group, index) in groups">
            <div :key="'group_' + index">
              <w-checkbox :label="group.userSite" :value="group.groupId" :model="schema.application.groups" />

              <div
                v-if="
                  group.userSiteHtmlDescription && schema.application.groups.$model.find(item => item === group.groupId)
                "
              >
                <div v-if="group.userSiteHtmlDescription.warning" class="media-information">
                  <p class="media-information__title">
                    Ważne
                  </p>

                  <html-or-content class="media-information__content" :value="group.userSiteHtmlDescription.warning" />

                  <v-expansion-panels
                    v-if="group.userSiteHtmlDescription.accordion"
                    flat
                    class="media-information__accordion-container"
                  >
                    <v-expansion-panel>
                      <v-expansion-panel-header expand-icon="none" class="media-information__accordion-activator">
                        <template v-slot:default="{ open }">
                          <span v-if="!open">{{ group.userSiteHtmlDescription.accordion.expandButtonTitle }}</span>

                          <span v-else>Zwiń </span>
                        </template>
                      </v-expansion-panel-header>

                      <v-expansion-panel-content class="media-information__accordion-content">
                        <html-or-content :value="group.userSiteHtmlDescription.accordion.title" />

                        <html-or-content :value="group.userSiteHtmlDescription.accordion.content" />
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>
              </div>
            </div>
          </template>

          <h3 class="mb-0 mt-2">Załączniki:</h3>

          <template v-if="newCardApplication">
            <w-checkbox :model="schema.application.physicalAttachments" />

            <div class="v-card-h4">
              Zaznaczenie tej opcji umożliwi złożenie wniosku bez załączników, jednak wymagana jest wizyta w punkcie
              wydawania kart i okazanie dokumentów potwierdzających uprawnienia celem jego akceptacji. W celu
              potwierdzenia uprawnień do Karnetu Kulturalnego oprócz dokumentów poświadczających uprawnienia do
              uzyskania Usteckiej Karty Mieszkańca, należy okazać odpowiednio zaświadczenie, decyzję, legitymację,
              orzeczenie lub kartę. W przypadku wyboru karty fizycznej wymagana będzie dwukrotna wizyta w punkcie
              wydawania kart - pierwsza w celu okazania dokumentów aby wniosek został zaakceptowany, druga w celu
              odbioru karty. Odbioru karty w formie karty fizycznej należy dokonać osobiście lub przez upoważnioną przez
              wnioskodawcę osobę, okazując dokument tożsamości. W imieniu niepełnoletniego dziecka, osoby będącej pod
              opieką prawną, kartę może odebrać rodzic lub opiekun prawny. Odbiór wymaga pokwitowania przez
              wnioskodawcę, rodzica, opiekuna prawnego lub osobę upoważnioną
            </div>
          </template>

          <div v-else>
            Przedłużenie ważności Usteckiej Karty Mieszkańca nastąpi po pozytywnej weryfikacji załączonych dokumentów.
          </div>

          <media :schema="schema.application.media" :multiple="true" />

          <consents-input :model="schema.application.terms" :terms-list="termsList" />

          <v-btn large min-width="250" elevation="0" color="primary" type="submit" class="mt-4">
            Złóż <template v-if="newCardApplication">wniosek</template>
          </v-btn>
        </div>
      </v-card>
    </v-col>
  </v-form>
</template>

<script>
import { ApplicationType, address, dateFilter } from '@witkac/kartamiejska-shared';
import { WCheckbox, WSelect } from '@witkac/vuetify-components';
import { mapState } from 'vuex';

import ConsentsInput from '@/components/forms/ConsentsInput';
import HtmlOrContent from '@/components/helpers/HtmlOrContent';
import { cardMixin } from '@/mixins/cardMixin';
import DisplayModelValue from './DisplayModelValue';
import Media from './Media';

const addressRequiredData = ['woj', 'pow', 'gmi', 'terc', 'city', 'simc', 'street', 'postalCode', 'homeNumber'];

// @vuese
// Formularz rejestracji
// @group Forms
export default {
  components: { HtmlOrContent, ConsentsInput, DisplayModelValue, Media, WSelect, WCheckbox },
  filters: { address },
  mixins: [cardMixin],
  props: {
    // schema
    schema: { type: Object, required: true }
  },
  data() {
    return {
      ApplicationType
    };
  },
  computed: {
    ...mapState({
      collectionPoints: state => state.collectionPoint.list,
      groups(state) {
        const key = this.newCardApplication ? 'APPLICATION_NEW_GROUPS_FORM' : 'APPLICATION_EXTEND_GROUPS_FORM';
        return state.settings[key]?.value;
      },
      termsList: state => state.terms.current
    }),
    addressError() {
      for (const key of addressRequiredData) {
        if (this.schema.application[key].$error) {
          return true;
        }
      }
      return false;
    },
    newCardApplication() {
      return this.schema.application.type.$model === ApplicationType[ApplicationType.NEW];
    },
    cardEndDate() {
      return dateFilter(this.card.endDate);
    }
  },
  methods: {
    // @vuese
    // Rejestracja wniosku
    submitApplication() {
      this.$emit('submit');
    }
  }
};
</script>

<style lang="scss">
.application-form .v-expansion-panel-header__icon {
  display: none !important;
}
.application-form .media-information__accordion-content li {
  padding-top: 10px;
}
</style>
